import React, { useMemo, useState } from "react";
import { Eye, EyeOff, Check, Circle, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { api, errMsg, TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { longDate } from "@/lib/sk";

const MIN = 12;

function PwInput({ id, label, value, onChange, autoComplete, testId, error }) {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-[12.5px]">{label}</Label>
      <div className="relative">
        <Input id={id} type={show ? "text" : "password"} autoComplete={autoComplete} value={value} onChange={(e) => onChange(e.target.value)} className={cn("h-10 rounded-[10px] pr-10", error && "border-[hsl(var(--v-blocked))]")} data-testid={testId} aria-invalid={Boolean(error)} />
        <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Skjul" : "Vis"} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground hover:bg-black/[0.05]" data-testid={`${testId}-toggle`}>{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
      </div>
      {error ? <p className="text-[12px] text-[hsl(var(--v-blocked))]" data-testid={`${testId}-error`}>{error}</p> : null}
    </div>
  );
}

/** Self-service password change. The user types everything; the server re-hashes, bumps the token version and logs out other sessions. */
export function ChangePasswordCard({ compact = false, onDone }) {
  const { user, applySession } = useAuth();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [rep, setRep] = useState("");
  const [busy, setBusy] = useState(false);
  const [serverErr, setServerErr] = useState(null);
  const [curErr, setCurErr] = useState(null);
  const checks = useMemo(() => {
    const low = pw.toLowerCase();
    const email = (user?.email || "").toLowerCase();
    const local = email.split("@")[0];
    const nameHit = (user?.name || "").toLowerCase().split(" ").some((p) => p.length > 2 && low.includes(p));
    return [
      [`Minst ${MIN} tegn`, pw.length >= MIN],
      ["Både bokstaver og tall", /[a-zæøå]/i.test(pw) && /\d/.test(pw)],
      ["Ikke e-post eller navn", pw.length > 0 && !(local && low.includes(local)) && !nameHit],
      ["Gjentatt likt", pw.length > 0 && pw === rep],
    ];
  }, [pw, rep, user]);
  const ok = checks.every(([, v]) => v) && cur.length > 0;
  const submit = async (e) => {
    e.preventDefault();
    if (!ok) return;
    setBusy(true); setServerErr(null); setCurErr(null);
    try {
      const r = await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      applySession(r.data.user);
      setCur(""); setPw(""); setRep("");
      toast.success("Passordet er endret. Alle andre innloggede økter er logget ut.");
      onDone?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 401) setCurErr(typeof d === "string" ? d : "Nåværende passord er feil");
      else if (d?.errors) setServerErr(d.errors.join(" · "));
      else setServerErr(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className={cn("rounded-[16px] border border-black/[0.06] bg-white p-4", compact && "border-0 p-0")} data-testid="change-password-card">
      {!compact ? (
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 text-[13.5px] font-semibold"><KeyRound className="h-4 w-4 text-muted-foreground" /> Passord</div>
          {user?.password_changed_at ? <span className="text-[12px] text-muted-foreground" data-testid="password-last-changed">Sist endret {longDate(user.password_changed_at)}</span> : <span className="v-badge v-assumed" data-testid="password-never-changed">Aldri endret – bytt det nå</span>}
        </div>
      ) : null}
      <div className={cn("grid gap-3", !compact && "mt-3 sm:grid-cols-2")}>
        <PwInput id="pw-current" label={user?.must_change_password ? "Midlertidig passord" : "Nåværende passord"} value={cur} onChange={setCur} autoComplete="current-password" testId="pw-current" error={curErr} />
        <div className="hidden sm:block" />
        <PwInput id="pw-new" label="Nytt passord" value={pw} onChange={setPw} autoComplete="new-password" testId="pw-new" />
        <PwInput id="pw-repeat" label="Gjenta nytt passord" value={rep} onChange={setRep} autoComplete="new-password" testId="pw-repeat" error={rep && rep !== pw ? "Passordene er ikke like" : null} />
      </div>
      <ul className="mt-3 grid gap-1 text-[12px] sm:grid-cols-2" data-testid="pw-checks">
        {checks.map(([label, v]) => <li key={label} className={cn("flex items-center gap-1.5", v ? "text-[hsl(var(--v-ok))]" : "text-muted-foreground")}>{v ? <Check className="h-3.5 w-3.5" /> : <Circle className="h-3 w-3" />} {label}</li>)}
      </ul>
      {serverErr ? <p className="mt-2 text-[12.5px] text-[hsl(var(--v-blocked))]" data-testid="pw-server-error">{serverErr}</p> : null}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11.5px] text-muted-foreground"><ShieldCheck className="mr-1 inline h-3.5 w-3.5" />Passordet lagres bare som hash. Andre økter logges ut når du bytter.</p>
        <button type="submit" disabled={!ok || busy} className="inline-flex h-9 items-center gap-2 rounded-full bg-[hsl(var(--navy))] px-4 text-[13px] font-semibold text-white disabled:opacity-40" data-testid="pw-submit">{busy ? "Lagrer…" : "Bytt passord"}</button>
      </div>
    </form>
  );
}
